import { ReactNode, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Box, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';

import { ColorModeContextProvider } from '@theme/ColorModeContext';

import { FallbackToplevel } from '@components/errorBoundary/FallbackToplevel';
import { getOktaAuthConfig, getAppEnvironment } from '@/config';
import { initDatadog } from '@/datadog';
import { ErrorListToast } from '@components/toast/ErrorListToast';
import { Toast } from '@components/toast/Toast';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { LinkToast } from '@components/toast/LinkToast';
import { ServiceWorkerUpdateRegistration } from '@/ServiceWorkerUpdateRegistration';
import AllRoutes from '@router/AllRoutes';
import { getDefaultEnvironment, useApiEnvStore } from '@utils/api-client';
import { useNavigate } from 'react-router-dom';
import { shallow } from 'zustand/shallow';
import { getTodaysHoliday, HolidayMode } from '@theme/palettes/getPalette';
import { Suspense } from 'react';
import LazyConfetti from '@components/holiday/LazyConfetti';
import { GhostAnimation } from '@components/holiday/GhostAnimation';
import { EasterEggHunt } from '@components/holiday/EasterEggHunt';

declare module 'notistack' {
  interface VariantOverrides {
    errorList: {
      errorList: string[];
      showSessionId?: boolean;
    };
    link: {
      link: ReactNode;
    };
    siteUpdateAlert: any;
    default: { showSessionId?: boolean };
    success: { showSessionId?: boolean };
    error: { showSessionId?: boolean };
    warning: { showSessionId?: boolean };
    info: { showSessionId?: boolean };
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (_, error) => {
        const { message } = error as Error;
        if (message.includes('400') || message.includes('404') || message.includes('403') || message.includes('401')) {
          return false;
        }
        return true;
      },
    },
  },
});

const oktaAuth = new OktaAuth(getOktaAuthConfig(window.lio.getApiEnvironment()));

console.log('app env', getAppEnvironment(), window.lio.getApiEnvironment(), getDefaultEnvironment());
console.log('oktaAuth', window.lio.getApiEnvironment());

const originalError = console.error;
console.error = function (...args) {
  if (typeof args[0] === 'string' && args[0].includes('MUI X: useResizeContainer - The parent DOM')) {
    return;
  }
  originalError.apply(console, args);
};

function App() {
  const navigate = useNavigate();
  const [disableHolidayModeFlag] = useApiEnvStore((state) => [state.disableHolidayModeFlag], shallow);

  useEffect(() => {
    initDatadog();
  }, []);

  const restoreOriginalUri = async (_oktaAuth: any, originalUri: any) => {
    // Guard against spurious calls that pass the site origin or no URI,
    // which would incorrectly navigate to '/'. Prefer the saved redirectUrl.
    const origin = window.location.origin;
    const isOriginOnly = typeof originalUri === 'string' && (originalUri === origin || originalUri === `${origin}/`);

    const storedRedirectUrl = localStorage.getItem('redirectUrl');
    const target = isOriginOnly ? storedRedirectUrl : originalUri || storedRedirectUrl;

    if (!target) {
      // Only default to home when handling the callback redirect (e.g. magic link flow).
      // During normal widget login, let the Login component's isAuthenticated guard handle the redirect.
      if (window.location.pathname === '/login/callback') {
        navigate('/');
      }
      return;
    }

    navigate(toRelativeUrl(target, origin));
  };

  const holiday = getTodaysHoliday();
  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeContextProvider>
        <ErrorBoundary FallbackComponent={FallbackToplevel}>
          <SnackbarProvider
            Components={{
              errorList: ErrorListToast,
              default: Toast,
              success: Toast,
              error: Toast,
              warning: Toast,
              info: Toast,
              link: LinkToast,
              siteUpdateAlert: Toast,
            }}
            dense
          >
            <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
              {holiday === 'AprilFools' && !disableHolidayModeFlag && (
                <Box
                  sx={{
                    width: '100vw',
                    height: '100vh',
                    position: 'fixed',
                  }}
                >
                  <Suspense fallback={null}>
                    <LazyConfetti gravity={0.05} opacity={0.8} numberOfPieces={400} run={true} />
                  </Suspense>
                </Box>
              )}

              {holiday === HolidayMode.Halloween && !disableHolidayModeFlag && <GhostAnimation />}

              {holiday === HolidayMode.Easter && !disableHolidayModeFlag && <EasterEggHunt />}

              <CssBaseline />

              <AllRoutes />
              <ServiceWorkerUpdateRegistration />
            </Security>
          </SnackbarProvider>
        </ErrorBoundary>
      </ColorModeContextProvider>
      {!getTodaysHoliday() && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
export default App;
